import React, { Dispatch, SetStateAction, useEffect, useMemo, useRef, useState } from "react";
import classNames from "classnames";
import { Form, FormControlProps } from "react-bootstrap";
import {
	Control,
	Controller,
	ControllerFieldState,
	ControllerRenderProps,
	UseFormStateReturn,
	useController,
} from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import Select, { ActionMeta, InputActionMeta, OptionsType, components } from "react-select";
import { AsyncPaginate, withAsyncPaginate } from "react-select-async-paginate";
import CreatableSelect from "react-select/creatable";
import { MenuListComponentProps, MenuListProps } from "react-select/src/components/Menu";
import useFlash from "go-alert/AlertMessage";
import handleError from "go-app/services/errors";
import EmptyData from "go-core/components/EmptyData";
import { ReactComponent as PreviewSVG } from "go-core/images/svg/preview.svg";
import { hasErrors } from "..";
import { useCustomValidation } from "../hooks";
import { CustomValidationConfig, CustomValidationConstraint } from "../services/types";
import { isConstraintARequiredConstraint } from "../services/utils";
import { FormErrorMessage } from "./FormErrorMessage";
import { useConfirmation } from "./ModalConfirm";

const parseToValues = (values: any, getOptionValue: (obj: any) => any) => {
	if (Array.isArray(values)) {
		return values.map((value) => {
			return getOptionValue(value);
		});
	}
	return getOptionValue(values);
};
const parseToObj = (values: any, defaultOptions: any, getOptionValue: (obj: any) => any, forceClear?: boolean) => {
	if (forceClear) return null;
	if (values && Array.isArray(values)) {
		let val = defaultOptions.filter((option: any) =>
			values.map((x) => getOptionValue(x)).includes(getOptionValue(option))
		);
		if (val.length === 0) {
			val = defaultOptions.filter((option: any) => values.map((x) => x).includes(getOptionValue(option)));
		}
		return val;
	}
	return values ? defaultOptions?.filter((option: any) => getOptionValue(option) === values)[0] : undefined;
};

export interface FormSelectGroupProps extends FormSelectProps {
	label?: string;
	errors: any;
	focus?: boolean;
	help?: string;
}

const customStyles = { menu: (styles: any) => ({ ...styles, zIndex: 999 }) };
const CreatableAsyncPaginate = withAsyncPaginate(CreatableSelect);

export const FormSelectGroup = (props: FormSelectGroupProps): JSX.Element => {
	const { label, name, help, errors, ...controlProps } = props;
	const isFieldRequired = useMemo(() => {
		return controlProps.customValidationConfig?.types.some((type) => {
			if (type.constraint === CustomValidationConstraint.REQUIRED) return true;
			if (
				type.constraint === CustomValidationConstraint.REQUIRED_IF ||
				type.constraint === CustomValidationConstraint.GREATER_THAN_IF_REQUIRED
			) {
				return type.isRequired();
			}

			return isConstraintARequiredConstraint(type.constraint);
		});
	}, [controlProps.customValidationConfig?.types]);

	return (
		<Form.Group className="form-group" controlId={name}>
			{label && <Form.Label>{isFieldRequired ? `${label} *` : label}</Form.Label>}

			{props.loadOptions ? (
				<FormSelect isInvalid={hasErrors(errors, name)} name={name} {...controlProps} />
			) : (
				<FormSelectSimple
					focus={props.focus}
					isInvalid={hasErrors(errors, name)}
					name={name}
					{...controlProps}
				/>
			)}
			{help && <Form.Text muted>{help}</Form.Text>}
			<FormErrorMessage errors={errors} name={name} />
		</Form.Group>
	);
};

interface FormSelectProps extends FormControlProps {
	options?: any;
	defaultValue?: any;
	loadOptions?: any;
	isInvalid?: boolean;
	isMulti?: boolean;
	parse?: (obj: any) => any;
	getOptionLabel?: (obj: any) => any;
	getOptionValue?: (obj: any) => any;
	onCreateOption?: (obj: any) => any;
	forceCreateOption?: boolean;
	onChange?: (obj: any, fullObj?: any) => any;
	path?: string;
	groupLabel?: string;
	placeholder?: string;
	focus?: boolean;
	name?: any;
	control?: Control<any>;
	onFocus?: () => void;
	isClearable?: boolean;
	disabled?: boolean;
	formatOptionLabel?: (option: any, labelMeta: any) => React.ReactNode;
	onMenuOpen?: (props: any) => Promise<any>;
	cacheUniqs?: any[];
	tabIndex?: number;
	onBlur?: () => void;
	openMenuOnFocus?: boolean;
	customActions?: JSX.Element[];
	toEditPath?: boolean;
	customValidationConfig?: CustomValidationConfig;
	forceClear?: boolean;
	onBlurShouldForceChangeOption?: boolean;
	forceInputChange?: boolean;
	isSearchable?: boolean;
	useConfirmationOnChange?: boolean;
	menuIsOpen?: boolean;
}

const SelectMenuError = (
	props: MenuListComponentProps<OptionsType<MenuListProps>, boolean> & {
		setForceSelectReloadKey: Dispatch<SetStateAction<number>>;
	}
) => {
	const { setForceSelectReloadKey } = props;
	const { t } = useTranslation();

	return (
		<components.MenuList {...props}>
			<EmptyData
				title={t("go_core.fetch_items_error.title", { ns: "lib" })}
				actions={[
					{
						name: t("common.action.close", { ns: "lib" }),
						click: () => setForceSelectReloadKey((prevKey: number) => ++prevKey),
					},
				]}
			/>
		</components.MenuList>
	);
};

const IconOption = (props: any) => {
	const {
		path,
		getOptionValue,
		components: { toEditPath },
	} = props.selectProps;
	const value = props.data;
	const finalPath = toEditPath
		? `${String(path) + getOptionValue(value)}/edit`
		: String(path) + getOptionValue(value);

	return (
		<components.Option {...props}>
			{value.__isNew__ ? (
				props.children
			) : (
				<div className="option-icon">
					<span>{props.children}</span>
					<Link rel="noreferrer" target="_blank" to={finalPath} className="preview-option">
						<PreviewSVG />
					</Link>
				</div>
			)}
		</components.Option>
	);
};
const ControlView = (props: any) => {
	const {
		path,
		getOptionValue,
		components: { customActions, toEditPath },
	} = props.selectProps;
	const value = props.getValue()[0];
	let finalPath = "";
	if (path) {
		finalPath = toEditPath ? `${String(path) + getOptionValue(value)}/edit` : String(path) + getOptionValue(value);
	}

	return (
		<components.Control {...props}>
			{props.children}
			{finalPath && (
				<Link
					rel="noreferrer"
					target="_blank"
					to={finalPath}
					className={classNames("preview", { disabled: !value })}
				>
					<PreviewSVG />
				</Link>
			)}
			{customActions}
		</components.Control>
	);
};

export const FormSelectSimple = (props: FormSelectProps): JSX.Element => {
	const selectRef: any = useRef(null);
	const {
		name,
		isClearable,
		disabled,
		isMulti,
		control,
		getOptionValue,
		isInvalid,
		placeholder,
		formatOptionLabel,
		onChange,
		options,
		tabIndex,
		onBlur,
		openMenuOnFocus,
		customValidationConfig,
		forceClear,
		useConfirmationOnChange,
		...controlProps
	} = props;
	const {
		field: { value },
	} = useController({
		name,
		control,
	});
	const { t } = useTranslation();
	const confirmation = useConfirmation();

	useEffect(() => {
		if (props.focus && selectRef && selectRef.current) {
			selectRef.current.focus();
		}
	});
	const getOptionValueInternal = (newValue: any) => {
		if (!newValue) {
			return null;
		}
		return getOptionValue ? getOptionValue(newValue) : newValue.value;
	};
	const handleChange = async (
		e: any,
		changeProps: {
			field: ControllerRenderProps<any, any>;
			fieldState: ControllerFieldState;
			formState: UseFormStateReturn<any>;
		}
	) => {
		try {
			let changeValue = null;
			if (e) {
				changeValue = parseToValues(e, getOptionValueInternal);
			}

			if (useConfirmationOnChange) {
				await confirmation({
					title: t("common.word.confirmation", { ns: "lib" }),
					message: t("confirmation.message.action", { ns: "lib" }),
				});
			}

			changeProps.field.onChange(changeValue);
			if (onChange) {
				onChange(changeValue);
			}
		} catch (err) {
			return null;
		}
	};

	useCustomValidation({ name, value, customValidationConfig });

	return (
		<Controller
			name={name}
			render={(controlerProps) => {
				const text1 = parseToObj(value, options, getOptionValueInternal, forceClear);
				return (
					<div data-testid={props["data-testid" as keyof FormSelectProps]}>
						<Select
							onMenuOpen={props.onMenuOpen && props ? () => props.onMenuOpen?.(props) : undefined}
							options={options}
							name={name}
							styles={customStyles}
							ref={selectRef}
							className={classNames("form-select", { "is-invalid": isInvalid })}
							classNamePrefix={"form-select"}
							onChange={(e) => handleChange(e, controlerProps)}
							onBlur={onBlur}
							value={text1}
							getOptionValue={getOptionValueInternal}
							formatOptionLabel={formatOptionLabel ? formatOptionLabel : undefined}
							isMulti={isMulti}
							isDisabled={disabled}
							isClearable={isClearable !== undefined ? isClearable : true}
							placeholder={placeholder || t("lib:common.action.select")}
							loadingMessage={() => `${t("lib:common.action.loading")}...`}
							tabIndex={tabIndex?.toString()}
							openMenuOnFocus={openMenuOnFocus}
							menuPlacement="auto"
							defaultValue={props.defaultValue}
							isSearchable={props.isSearchable}
							menuIsOpen={props.menuIsOpen}
							// onFocus={() => {
							//     // if(selectRef && selectRef.current) {
							//     //     console.log(selectRef);
							//     //     selectRef.current.focus()
							//     // }
							// }}
						/>
					</div>
				);
			}}
			onFocus={() => {
				if (selectRef && selectRef.current) {
					selectRef.current.focus();
				}
			}}
			control={control}
			{...controlProps}
		/>
	);
};
export const FormSelect = (props: FormSelectProps): JSX.Element => {
	const {
		name,
		isClearable,
		disabled,
		isMulti,
		control,
		getOptionLabel,
		getOptionValue,
		onCreateOption,
		forceCreateOption,
		defaultValue,
		groupLabel,
		isInvalid,
		placeholder,
		loadOptions,
		path,
		onChange,
		formatOptionLabel,
		onMenuOpen,
		cacheUniqs,
		tabIndex,
		openMenuOnFocus,
		toEditPath,
		customValidationConfig,
		onBlurShouldForceChangeOption,
		forceInputChange,
		useConfirmationOnChange,
		...controlProps
	} = props;
	const {
		field: { value },
	} = useController({
		name,
		control,
	});

	const [defaultOptions, setDefaultOptions] = useState<any[]>([]);
	const page = useRef<number>(0);
	const [search, setSearch] = useState<string>("");
	const [innerInputLabel, setInnerInputLabel] = useState(value);
	const [internalOptions, setInternalOptions] = useState<any[]>([]);
	const [fetchOptionsErrorOccurred, setFetchOptionsErrorOccurred] = useState(false);
	const [forceSelectReloadKey, setForceSelectReloadKey] = useState(0);
	const defaultValues = Array.isArray(defaultValue) ? [...defaultValue] : [defaultValue];
	const { t } = useTranslation();
	const { addFlash } = useFlash();
	const confirmation = useConfirmation();

	const getOptionLabelInternal = (optionValue: any) => {
		if (!optionValue) {
			return null;
		}
		if (optionValue.__isNew__) {
			return optionValue.label;
		}
		return getOptionLabel ? getOptionLabel(optionValue) : optionValue.label;
	};

	const getNewOptionDataInternal = (inputValue: any, optionLabel: any) => {
		return {
			id: inputValue,
			name: optionLabel,
		};
	};

	const getOptionValueInternal = (optionValue: any) => {
		if (!optionValue) {
			return null;
		}
		return getOptionValue ? getOptionValue(optionValue) : optionValue.value;
	};

	const loadOptionsInternal = async (data: any, scrollData: Array<{}>) => {
		try {
			if (scrollData.length % 20 === 0 && scrollData.length > 0) {
				page.current++;
			}
			if (data.toLowerCase() !== search.toLowerCase()) {
				page.current = 0;
			}
			setSearch(data);
			const response = await loadOptions(data, {
				page: page.current,
			});
			if (response.length === 0) page.current = 0;
			const resOptions = response.map((item: any) => {
				const newItem = { ...item };
				delete newItem.options;
				if (groupLabel) {
					newItem.options = item[groupLabel];
				}
				return newItem;
			});

			setFetchOptionsErrorOccurred(false);
			setInternalOptions([...resOptions]);

			return {
				options: [...resOptions],
				hasMore: resOptions.length === 20,
			};
		} catch (err) {
			handleError.alert(err, addFlash);
			setFetchOptionsErrorOccurred(true);
			return { options: [], hasMore: false };
		}
	};

	const [text, setText] = useState(parseToObj(value, defaultValues, getOptionValueInternal));

	useCustomValidation({ name, value, customValidationConfig });

	useEffect(() => {
		const newText = parseToObj(value, defaultValues, getOptionValueInternal);
		if (newText?.label && newText?.id) {
			setText(newText);
		}
	}, [value, defaultValues]);

	const handleChange = async (
		e: (value: any, action: ActionMeta<any>) => void,
		changeProps: {
			field: ControllerRenderProps<any, any>;
			fieldState: ControllerFieldState;
			formState: UseFormStateReturn<any>;
		}
	) => {
		try {
			setText(e);
			let changeValue = null;
			if (e) {
				changeValue = parseToValues(e, getOptionValueInternal);
			}

			if (useConfirmationOnChange) {
				await confirmation({
					title: t("common.word.confirmation", { ns: "lib" }),
					message: t("confirmation.message.action", { ns: "lib" }),
				});
			}

			changeProps.field.onChange(changeValue);
			if (onChange) {
				onChange(changeValue, e);
			}

			if (forceInputChange) {
				if (e === null) {
					setInnerInputLabel("");
				} else {
					setInnerInputLabel(getOptionLabelInternal(e));
				}
			}
		} catch (err) {
			return null;
		}
	};

	const handleOnBlur = (
		event: React.FocusEvent<HTMLInputElement>,
		changeProps: {
			field: ControllerRenderProps<any, any>;
			fieldState: ControllerFieldState;
			formState: UseFormStateReturn<any>;
		}
	) => {
		if (onBlurShouldForceChangeOption && !isMulti) {
			const eventValue = event.target.value;
			const possibleOption = internalOptions.find((o) => getOptionLabelInternal(o) === eventValue);

			if (changeProps.field.value === undefined) return;
			if (eventValue === changeProps.field.value) return;

			if (possibleOption) {
				handleChange(possibleOption, changeProps);
			} else if (forceCreateOption) {
				handleChange(
					{
						label: eventValue,
						value: eventValue,
						__isNew__: true,
					} as any,
					changeProps
				);
			}
		}
	};

	let customComponents: Record<string, any> = {
		Control: ControlView,
		customActions: props.customActions,
	};
	if (path) {
		customComponents = {
			...customComponents,
			Option: IconOption,
			toEditPath,
		};
	}
	if (fetchOptionsErrorOccurred) {
		customComponents.MenuList = (props: MenuListComponentProps<OptionsType<MenuListProps>, boolean>) => (
			<SelectMenuError {...props} setForceSelectReloadKey={setForceSelectReloadKey} />
		);
	}

	const formatGroupLabel = (data: Record<string, any>) => {
		return (
			<div>
				<span>{data.label}</span>
				<span>{data.options.length}</span>
			</div>
		);
	};

	const onFocusInternal = async () => {
		if (defaultOptions.length <= 0 && !onMenuOpen) {
			const values = await loadOptionsInternal("", []);
			setDefaultOptions(values.options);
		}
	};

	const loadOptionsOnMenuOpen = async () => {
		const values = await loadOptionsInternal(forceInputChange ? innerInputLabel : "", []);
		setDefaultOptions(values.options);
	};

	const handleOnInputChange = (text: string, action: InputActionMeta) => {
		if (!forceInputChange) return;

		if (action.action === "input-change") {
			setInnerInputLabel(text);
			return text;
		}
		return innerInputLabel;
	};

	return (
		<Controller
			name={name}
			render={(controlProps) => {
				let text1 = parseToObj(value, defaultValues, getOptionValueInternal);
				let text2 = text;
				if (isMulti && onCreateOption) {
					text2 = text1;
				}
				if (!onCreateOption) {
					text1 = text;
				}
				if (onCreateOption && !value) {
					text1 = text;
				}
				if (!value) {
					text1 = null;
					text2 = null;
				}

				return (
					<div data-testid={props["data-testid" as keyof FormSelectProps]}>
						{onCreateOption || forceCreateOption ? (
							<CreatableAsyncPaginate
								isSearchable={props.isSearchable}
								loadOptions={loadOptionsInternal}
								// defaultOptions={defaultOptions}
								// onFocus={onFocusInternal}
								onMenuOpen={onMenuOpen ? () => loadOptionsOnMenuOpen() : undefined}
								path={path}
								styles={customStyles}
								getOptionValue={getOptionValueInternal}
								getOptionLabel={getOptionLabelInternal}
								formatOptionLabel={formatOptionLabel ? formatOptionLabel : undefined}
								components={customComponents}
								onCreateOption={onCreateOption}
								formatGroupLabel={formatGroupLabel}
								cacheUniqs={[
									search,
									onBlurShouldForceChangeOption && !isMulti ? text1 : "",
									innerInputLabel,
									...(cacheUniqs || []),
								]}
								isMulti={isMulti}
								createOptionPosition="first"
								className={classNames("form-select", {
									"is-invalid": isInvalid,
									"is-multi": isMulti,
								})}
								classNamePrefix={"form-select"}
								onChange={(e) => handleChange(e, controlProps)}
								onBlur={(e) => handleOnBlur(e as React.FocusEvent<HTMLInputElement>, controlProps)}
								value={isMulti ? text2 : text1}
								inputValue={forceInputChange ? innerInputLabel : undefined}
								onInputChange={handleOnInputChange}
								placeholder={placeholder || t("lib:common.action.select")}
								isDisabled={disabled}
								controlShouldRenderValue={!forceInputChange}
								isClearable={isClearable === undefined || isClearable}
								formatCreateLabel={() => `${t("lib:common.action.create")} "${search}"`}
								loadingMessage={() => `${t("lib:common.action.loading")}...`}
								tabIndex={tabIndex?.toString()}
								openMenuOnFocus={openMenuOnFocus}
								menuPlacement="auto"
								key={forceSelectReloadKey}
							/>
						) : (
							<AsyncPaginate
								isSearchable={props.isSearchable}
								loadOptions={loadOptionsInternal}
								defaultOptions={defaultOptions}
								path={path}
								cacheUniqs={[search, ...(cacheUniqs || [])]}
								getNewOptionData={getNewOptionDataInternal}
								getOptionValue={getOptionValueInternal}
								getOptionLabel={getOptionLabelInternal}
								formatOptionLabel={formatOptionLabel ? formatOptionLabel : undefined}
								className={classNames("form-select", {
									"is-invalid": isInvalid,
									"is-multi": isMulti,
								})}
								classNamePrefix={"form-select"}
								styles={customStyles}
								components={customComponents}
								isMulti={isMulti}
								onChange={(e) => handleChange(e, controlProps)}
								value={isMulti ? text2 : text1}
								placeholder={placeholder || t("lib:common.action.select")}
								isDisabled={disabled}
								isClearable={isClearable === undefined || isClearable}
								formatCreateLabel={() => `${t("lib:common.action.create")} "${search}"`}
								loadingMessage={() => `${t("lib:common.action.loading")}...`}
								tabIndex={tabIndex?.toString()}
								openMenuOnFocus={openMenuOnFocus}
								menuPlacement="auto"
								key={forceSelectReloadKey}
							/>
						)}
					</div>
				);
			}}
			control={control}
			{...controlProps}
		/>
	);
};
